import { Injectable } from "@angular/core";
import { Router, UrlTree } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { MonitoringService } from "src/app/service/monitoring.service";
import { LoadedContextService } from "./loadedContext.service";
import { OutlookService } from "./outlook/outlook.service";
import { OutlookSharedContextService } from "./outlook/outlook-shared-context.service";
import { AuthService } from "./auth.service";
import { GraphService } from "./graph/graph.service";
import { SharedService } from "./shared.service";

@Injectable({
  providedIn: "root"
})
export class OfficeJSGuardService {
  /** How long to wait for Office.js to surface a usable mail item before giving up. */
  static readonly itemAvailabilityTimeoutMs = 10000;

  /**
   * Shorter wait for the known-broken state: item present with itemId === "" and an
   * internetMessageId to recover from (S/MIME on Outlook for Mac). The empty id has not
   * been observed to fill in later, so a brief grace period is enough before recovering.
   */
  static readonly emptyItemIdGraceMs = 2000;

  constructor(
    private router: Router,
    private monitoringService: MonitoringService,
    private loadedContextService: LoadedContextService,
    private outlookService: OutlookService,
    private sharedContextService: OutlookSharedContextService,
    private authService: AuthService,
    private graphService: GraphService,
    private sharedService: SharedService
  ) { }

  async canAccessOffice365(): Promise<boolean | UrlTree> {
    if (!this.loadedContextService.isComposeMode()) {
      const { ready, waitedMs } = await this.waitForUsableItem();

      if (!ready) {
        // Outlook never surfaced a usable item (e.g. S/MIME signed emails on Outlook for Mac
        // expose the item with itemId === ""). Try resolving the id via Graph before failing.
        const recovered = await this.tryRecoverItemRestId(waitedMs);
        if (!recovered) {
          return this.router.parseUrl("/error/general");
        }
      }
    }

    // Compose mode
    const mailItem = this.outlookService.mailboxItem;
    if (mailItem && !mailItem.displayReplyForm) {
      // Check if mail client is Outlook desktop client (Windows and Mac) and support for office API 1.8
      if (this.outlookService.mailbox.diagnostics.hostName === "Outlook" && !this.outlookService.isRequirementSet18Supported) {
        return this.router.parseUrl("/error/not-office365-client");
      }
    }

    if (!this.outlookService.isRequirementSet16Supported) {
      return true;
    }

    // 2023-06-28 for some reason on iOS userProfile is {}
    // for now, ignore the check as it prevents app from starting and log the contents of userProfile
    const profile = this.outlookService.userProfile;
    if (profile.accountType !== "office365" && this.outlookService.mailbox.diagnostics.hostName === "OutlookIOS") {
      this.monitoringService.logFailed("OfficeJSGuardService.canAccessOffice365", JSON.stringify(profile));
      return true; // better safe than sorry
    }

    if (profile.accountType === "office365") {
      const isShared = await this.sharedContextService.initializeSharedContextAsync();
      if (isShared) {
        this.authService.ensureSharedMailboxScope();
      }
      return true;
    } else {
      return this.router.parseUrl("/error/not-subscriber");
    }
  }

  /**
   * Fallback for items where Office.js withholds the itemId: resolves the REST id
   * via Graph by internetMessageId and stores it for SharedService.getItemRestId().
   * Returns true when the app can proceed with the recovered id.
   */
  private async tryRecoverItemRestId(waitedMs: number): Promise<boolean> {
    const item = this.outlookService.mailboxItem;
    const diagnostics = {
      itemNull: item === null || item === undefined,
      itemId: item?.itemId === undefined ? "undefined" : JSON.stringify(item.itemId),
      itemType: item?.itemType,
      waitedMs
    };

    const internetMessageId = item?.internetMessageId;
    if (!internetMessageId) {
      this.monitoringService.logCustomEvent("OfficeItemUnavailable", { ...diagnostics, recovery: "noInternetMessageId" });
      return false;
    }

    try {
      const restId = await firstValueFrom(this.graphService.findMessageIdByInternetMessageId(internetMessageId));
      if (!restId) {
        this.monitoringService.logCustomEvent("OfficeItemUnavailable", { ...diagnostics, recovery: "graphNoMatch" });
        return false;
      }

      this.sharedService.setItemRestIdFallback(internetMessageId, restId);
      this.monitoringService.logCustomEvent("OfficeItemIdRecovered", diagnostics);
      return true;
    } catch (error) {
      this.monitoringService.logCustomEvent("OfficeItemUnavailable", { ...diagnostics, recovery: "graphFailed", error: String(error) });
      return false;
    }
  }

  /**
   * Polls until the mail item is usable (has an itemId, or is a compose item).
   * Resolves { ready: false, waitedMs } if the wait times out, with waitedMs being the
   * actual elapsed time (not a constant) — callers must handle the failure instead of
   * assuming the item is available. The known-broken state (item present with
   * itemId === "" and a recoverable internetMessageId) only gets a short grace
   * period; the full timeout applies while the item is still hydrating. Because the
   * item can transition between these states mid-wait, the applicable timeout is
   * re-evaluated on every tick.
   */
  private async waitForUsableItem(): Promise<{ ready: boolean; waitedMs: number }> {
    const start = Date.now();

    for (;;) {
      const item = this.outlookService.mailboxItem;
      if (item?.itemId || (item && !item.displayReplyForm)) {
        return { ready: true, waitedMs: Date.now() - start };
      }

      const elapsed = Date.now() - start;
      const isRecoverableEmptyId = !!item && item.itemId === "" && !!item.internetMessageId;
      const timeoutMs = isRecoverableEmptyId
        ? OfficeJSGuardService.emptyItemIdGraceMs
        : OfficeJSGuardService.itemAvailabilityTimeoutMs;

      if (elapsed >= timeoutMs) {
        return { ready: false, waitedMs: elapsed };
      }

      await this.delay(50);
    }
  }

  private async delay(ms: number) {
    await new Promise(res => setTimeout(res, ms));
  }
}
